import React, { useEffect, useState } from "react";
import { Form, Button } from "react-bootstrap";
import DocumentUploader from "../../../components/DocumentDropZoneUploader";
import { useTranslation } from "react-i18next";
import Breadcrumbs from "../../../components/Breadcrumbs";
import {
  getEmployeeOnboardingDocuments,
  getOnboardingRequiredDocuments,
  submitOnboardingDocuments,
  updateDocument,
} from "../../../services/onBoardingServices";
import iconMap from "../../../config/iconConfig";
import toast from "react-hot-toast";
import { AiOutlineExclamationCircle } from "react-icons/ai";
import { useLocation } from "react-router-dom";

const UploadOnboardingDocuments = () => {
  const [uploads, setUploads] = useState({});
  const [requiredDocuments, setRequiredDocuments] = useState();
  const [editMode, setEditMode] = useState(false);
  const [loader, setLoader] = useState(false);
  const { t } = useTranslation();
  const loginEmployee = localStorage.getItem("employeeID");
  const location = useLocation();
  const stepData = location?.state?.step;

  const fetchEmployeeDocuments = async (loginEmployee) => {
    try {
      const result = await getEmployeeOnboardingDocuments(loginEmployee);
      return result?.employeeDocuments ?? {};
    } catch (error) {
      console.error(error);
    }
  };

  const fetchOnboardingDocuments = async () => {
    setLoader(true);

    try {
      const result = await getOnboardingRequiredDocuments(loginEmployee);
      if (result?.success === true) {
        setRequiredDocuments(result?.requiredDocuments);
        const employeeDocs = await fetchEmployeeDocuments(loginEmployee);
        if (Object.keys(employeeDocs ?? {})?.length > 0) {
          setUploads(employeeDocs);
          setEditMode(true);
        } else {
          const initialUploads = result.requiredDocuments.reduce((acc, doc) => {
            acc[doc.documentKey] = {
              document_type_id: doc.documentTypeId.toString(),
              
              file: null,
              ...(doc.requiresNumber && { document_number: "" }),
            };
            return acc;
          }, {});
          console.log("initialUploads" , initialUploads);
          setUploads(initialUploads);
          setEditMode(false);
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    if (loginEmployee) {
      fetchOnboardingDocuments();
    }
  }, [loginEmployee]);

  const handleFileSelect = (key, file) => {
    setUploads((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        file,
      },
    }));
  };

  const handleRemoveFile = (key) => {
    setUploads((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        file: null,
      },
    }));
  };

  console.log("submitOnboardingDocuments" ,  requiredDocuments)
 const submitDocuments = async () => {
  setLoader(true);
  try {
    const missingFields = [];
    const formData = new FormData();
    const metadata = {};

    requiredDocuments.forEach((doc) => {
      const uploadedDoc = uploads[doc.documentKey];
      const hasFile = uploadedDoc?.file instanceof File;
      const hasDocNumber = uploadedDoc?.document_number?.trim();

      // ---------- 1️⃣ VALIDATION RULES ----------
      if (doc.isMandatory) {
        // Mandatory doc must have a file
        if (!hasFile) {
          missingFields.push(`${doc.documentName} file`);
        }
        // If requires number, must have number too
        if (doc.requiresNumber && !hasDocNumber) {
          missingFields.push(`${doc.documentName} number`);
        }
      }

      // If file exists and requires number, number must also exist
      if (hasFile && doc.requiresNumber && !hasDocNumber) {
        missingFields.push(`${doc.documentName} number (required because file is uploaded)`);
      }

      // ---------- 2️⃣ APPEND ONLY VALID DOCUMENTS ----------
      if (hasFile) {
        metadata[doc.documentKey] = {
          document_type_id: uploadedDoc?.document_type_id,
          document_number: hasDocNumber || null,
        };
        formData.append(doc.documentKey, uploadedDoc.file);
      }
    });

    // ---------- 3️⃣ SHOW ERROR IF MISSING MANDATORY FIELDS ----------
    if (missingFields.length > 0) {
      toast.error("Missing Required Fields");
      setLoader(false);
      return;
    }

    // ---------- 4️⃣ SUBMIT FORM ----------
    formData.append("documents", JSON.stringify(metadata));

    const result = await submitOnboardingDocuments(
      loginEmployee,
      formData,
      stepData?.stepId
    );

    await fetchOnboardingDocuments();

    toast.success("Documents uploaded successfully");
  } catch (error) {
    console.error("Document submission error:", error);
    toast.error("Something went wrong");
  } finally {
    setLoader(false);
  }
};



  const reUploadDocument = async (data) => {
    setLoader(true);
    try {
      const formData = new FormData();
      formData.append("file", data?.file);
      formData.append("document_number", data?.document_number);

      const result = await updateDocument(
        loginEmployee,
        data?.document_id,
        formData
      );
      if (result?.success == true) {
        await fetchOnboardingDocuments();
        toast.success("Document Updated successfully");
      }
    } catch (error) {
      toast.error("Something went wrong");
    } finally {
      setLoader(false);
    }
  };

  return (
    <div className="page-wrapper" style={{ position: "relative" }}>
      {/* Page Content */}
      <div className="content container-fluid">
        <Breadcrumbs
          maintitle={t("Upload documents")}
          title={t("Onboarding")}
          subtitle={t("Upload documents")}
          titleLink="onboarding"
        />
        <div className="mb-4">
          <h3 className="fw-bold">Required Documents</h3>
          <p className="text-muted mb-0">
            To complete your verification, please upload the following
            documents.
          </p>
        </div>

        {requiredDocuments?.map((doc) => {
          const IconComponent = iconMap[doc.documentIcon];
          return (
            <div className="mb-4" key={doc.documentKey}>
              <div className="d-flex gap-3 align-items-start mb-2">
                <div
                  className="bg-secondary text-white rounded-circle d-flex align-items-center justify-content-center"
                  style={{ width: "36px", height: "36px" }}
                >
                  {IconComponent ? (
                    <IconComponent size={20} />
                  ) : (
                    <div
                      className="rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center"
                      style={{
                        width: 40,
                        height: 30,
                        fontSize: "1.5rem",
                      }}
                    >
                      {doc.documentName[0]}
                    </div>
                  )}
                </div>
                <div>
                  <div className="fw-semibold">
                    {doc.documentName}{" "}
                    {doc.isMandatory && <span className="text-danger">*</span>}
                  </div>
                  <div className="text-muted small">{doc.note}</div>
                  <div>
                    <span className="fw-semibold">Verification status:</span>
                    <span
                      className={`ms-1 badge badge-${
                        uploads[doc.documentKey]?.verification?.status ==
                        "rejected"
                          ? "danger"
                          : uploads[doc.documentKey]?.verification?.status ==
                            "approved"
                          ? "success"
                          : "warning"
                      }`}
                    >
                      {uploads[doc.documentKey]?.verification?.status ?? "-"}
                    </span>
                  </div>
                </div>
              </div>
              {doc.requiresNumber && (
                <div className="col-sm-6">
                  <Form.Label>
                    Document Number {doc?.isMandatory && <span className="text-danger">*</span>}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter Document Number"
                    className="mb-2 w-100 "
                    disabled={
                      uploads[doc.documentKey]?.verification?.status !==
                        "pending" &&
                      uploads[doc.documentKey]?.verification?.status !==
                        "approved"
                        ? false
                        : true
                    }
                    onChange={(e) =>
                      setUploads((prev) => ({
                        ...prev,
                        [doc.documentKey]: {
                          ...prev[doc.documentKey],
                          document_number: e.target.value,
                        },
                      }))
                    }
                    value={uploads[doc.documentKey]?.document_number || ""}
                  />
                </div>
              )}
              {uploads[doc.documentKey]?.verification?.status ===
                "rejected" && (
                <div>
                  <AiOutlineExclamationCircle
                    size={16}
                    className="text-danger me-1"
                  />
                  <span className="me-2 text-danger">Issue : </span>
                  {uploads[doc.documentKey]?.issues[0]?.reason ?? ""}
                </div>
              )}
              <DocumentUploader
                documentKey={doc.documentKey}
                onFileSelect={handleFileSelect}
                uploadedFile={uploads[doc.documentKey]?.file}
                onRemove={handleRemoveFile}
                allowRemove={
                  uploads[doc.documentKey]?.verification?.status !==
                    "pending" &&
                  uploads[doc.documentKey]?.verification?.status !== "approved"
                }
              />

              {uploads[doc.documentKey]?.verification?.status ===
                "rejected" && (
                <div className="d-flex justify-content-end">
                  <button
                    className="btn btn-primary"
                    onClick={() => reUploadDocument(uploads[doc.documentKey])}
                  >
                    {loader ? "Updating..." : "Update"}
                  </button>
                </div>
              )}
            </div>
          );
        })}
        {!editMode && (
          <div className="d-flex justify-content-end">
            <Button
              variant="primary"
              className="bor-rad-50"
              onClick={submitDocuments}
              disabled={loader ? true : false}
            >
              {loader ? "Saving..." : "Save documents"}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default UploadOnboardingDocuments;
